import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, Send, Inbox, CheckCircle, XCircle, Clock, Search, RefreshCw, User } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import NavigationMenu from '../components/NavigationMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagesAdminPage = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [messages, filterStatus, searchQuery]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/support/messages/all`, { withCredentials: true });
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = messages;
    
    // Filtro por status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(m => m.status === filterStatus);
    }
    
    // Filtro por busca
    if (searchQuery) {
      filtered = filtered.filter(m => 
        m.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.message?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredMessages(filtered);
  };

  const handleRespond = async () => {
    if (!responseText.trim()) {
      toast.error('Escreva uma resposta');
      return;
    }
    
    try {
      setResponding(true);
      await axios.post(
        `${API}/support/messages/${selectedMessage.id}/respond`,
        { message_id: selectedMessage.id, response: responseText },
        { withCredentials: true }
      );
      
      toast.success('Resposta enviada!');
      setResponseText('');
      setShowResponseDialog(false);
      fetchMessages();
    } catch (error) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setResponding(false);
    }
  };

  const updateStatus = async (messageId, newStatus) => {
    try {
      await axios.put(
        `${API}/support/messages/${messageId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      fetchMessages();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      novo: 'bg-blue-600',
      lido: 'bg-yellow-600',
      respondido: 'bg-green-600',
      fechado: 'bg-gray-600'
    };
    return <Badge className={styles[status] || 'bg-gray-600'}>{status}</Badge>;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'novo': return <Mail className="w-5 h-5 text-blue-600" />;
      case 'lido': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'respondido': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'fechado': return <XCircle className="w-5 h-5 text-gray-600" />;
      default: return <Mail className="w-5 h-5" />;
    }
  };

  const stats = {
    total: messages.length,
    novo: messages.filter(m => m.status === 'novo').length,
    respondido: messages.filter(m => m.status === 'respondido').length,
    fechado: messages.filter(m => m.status === 'fechado').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <NavigationMenu />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <NavigationMenu />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-amber-900 mb-2">
            <Inbox className="w-10 h-10 inline-block mr-3" />
            Mensagens de Suporte
          </h1>
          <p className="text-gray-600">Gerencie mensagens dos usuários</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border-2 border-gray-200">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </Card>
          <Card className="p-4 border-2 border-blue-200 bg-blue-50">
            <p className="text-sm text-blue-700">Novas</p>
            <p className="text-2xl font-bold text-blue-900">{stats.novo}</p>
          </Card>
          <Card className="p-4 border-2 border-green-200 bg-green-50">
            <p className="text-sm text-green-700">Respondidas</p>
            <p className="text-2xl font-bold text-green-900">{stats.respondido}</p>
          </Card>
          <Card className="p-4 border-2 border-gray-200">
            <p className="text-sm text-gray-600">Fechadas</p>
            <p className="text-2xl font-bold text-gray-900">{stats.fechado}</p>
          </Card>
        </div>

        {/* Filtros */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar por nome, email ou assunto..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => setFilterStatus('all')}
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                size="sm"
              >
                Todas
              </Button>
              <Button
                onClick={() => setFilterStatus('novo')}
                variant={filterStatus === 'novo' ? 'default' : 'outline'}
                size="sm"
                className={filterStatus === 'novo' ? 'bg-blue-600' : ''}
              >
                Novas
              </Button>
              <Button
                onClick={() => setFilterStatus('respondido')}
                variant={filterStatus === 'respondido' ? 'default' : 'outline'}
                size="sm"
                className={filterStatus === 'respondido' ? 'bg-green-600' : ''}
              >
                Respondidas
              </Button>
              <Button onClick={fetchMessages} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Lista de Mensagens */}
        <div className="space-y-3">
          {filteredMessages.length === 0 ? (
            <Card className="p-8 text-center">
              <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Nenhuma mensagem encontrada</p>
            </Card>
          ) : (
            filteredMessages.map((msg) => (
              <Card
                key={msg.id}
                className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                  msg.status === 'novo' ? 'border-2 border-blue-300 bg-blue-50' : 'border border-gray-200'
                }`}
                onClick={() => {
                  setSelectedMessage(msg);
                  setShowResponseDialog(true);
                  if (msg.status === 'novo') {
                    updateStatus(msg.id, 'lido');
                  }
                }}
              >
                <div className="flex items-start gap-4">
                  {getStatusIcon(msg.status)}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="font-semibold text-gray-900">{msg.user_name}</span>
                      <span className="text-sm text-gray-500">({msg.user_email})</span>
                    </div>
                    
                    <p className="font-medium text-gray-800 mb-1">{msg.subject || 'Sem assunto'}</p>
                    <p className="text-sm text-gray-600 line-clamp-2">{msg.message}</p>
                    
                    <div className="flex items-center gap-2 mt-2">
                      {getStatusBadge(msg.status)}
                      <span className="text-xs text-gray-500">
                        {new Date(msg.created_at).toLocaleString('pt-BR')}
                      </span>
                      {msg.responses?.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {msg.responses.length} {msg.responses.length === 1 ? 'resposta' : 'respostas'}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Dialog Ver/Responder */}
        <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Mensagem de {selectedMessage?.user_name}
              </DialogTitle>
              <DialogDescription>
                {selectedMessage?.user_email} • {selectedMessage?.created_at && new Date(selectedMessage.created_at).toLocaleString('pt-BR')}
              </DialogDescription>
            </DialogHeader>

            {selectedMessage && (
              <div className="space-y-4">
                {/* Mensagem Original */}
                <Card className="p-4 bg-amber-50 border-amber-200">
                  <p className="font-semibold text-amber-900 mb-2">Assunto: {selectedMessage.subject || 'Sem assunto'}</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                </Card>

                {/* Respostas Anteriores */}
                {selectedMessage.responses?.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-gray-900">Respostas:</h3>
                    {selectedMessage.responses.map((resp, idx) => (
                      <Card key={idx} className="p-4 bg-green-50 border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-green-600">Admin</Badge>
                          <span className="text-sm text-gray-600">
                            {new Date(resp.created_at).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{resp.response}</p>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Nova Resposta */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sua Resposta:
                  </label>
                  <Textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Digite sua resposta..."
                    rows={4}
                    className="mb-2"
                  />
                </div>

                {/* Ações de Status */}
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => updateStatus(selectedMessage.id, 'lido')}
                    variant="outline"
                    size="sm"
                    className="border-yellow-500 text-yellow-700"
                  >
                    Marcar como Lido
                  </Button>
                  <Button
                    onClick={() => updateStatus(selectedMessage.id, 'fechado')}
                    variant="outline"
                    size="sm"
                    className="border-gray-500 text-gray-700"
                  >
                    Fechar Ticket
                  </Button>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResponseDialog(false)}>
                Fechar
              </Button>
              <Button 
                onClick={handleRespond}
                disabled={responding || !responseText.trim()}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {responding ? 'Enviando...' : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Resposta
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MessagesAdminPage;
